'use client'

import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { AFTER_FILTER_PRODUCTS } from 'product-list/constants/productCards'
import { useProductList } from 'product-list/hooks/useProductList/useProductList'
import { usePushFiltersSearchParams } from 'product-list/hooks/usePushFiltersSearchParams/usePushFiltersSearchParams'
import {
	FilterId,
	FilterType,
	type RangePickerValue,
	type SerializedFilters,
	type UseFiltersData,
	type UseFiltersSlotItem,
	type UserFilter,
} from 'product-list/types/Filters'
import type {
	OrderMethod,
	ProductListFiltersRef,
	ProductListItem,
	ProductListParams,
} from 'product-list/types/ProductsList'
import {
	createContext,
	type Dispatch,
	forwardRef,
	type ReactNode,
	type SetStateAction,
	useCallback,
	useImperativeHandle,
	useMemo,
	useState,
} from 'react'
import { ORDER_OPTIONS, RECOMMENDED } from './constants'
import { getDisabledValues } from './disabledValues'
import { filterItems } from './filterItems'
import { serialize } from './serializer/serializer'
import { useFiltersDefaultValues } from './useFiltersDefaultValues'
import { useFiltersValue } from './useFiltersValue'
import { countActiveFilters, getHasSelection } from './utils'

export interface ProductListFiltersContextType {
	selectedFilters: UserFilter[]
	orderFilter: UserFilter
	selectedOrder: OrderMethod
	selectedRange?: RangePickerValue
	selectedLimitRange?: RangePickerValue
	selectedCount: number
	hasSelection: boolean
	disabledValues: string[]
	nAvailableFilters: number
	nActiveFilters: number
	filteredItemIdSet: Set<UseFiltersSlotItem['id']>
	currentOrder: OrderMethod
	hasInitialParams: boolean
	setSelectedRange: (range: RangePickerValue) => void
	setSelectedOrder: Dispatch<SetStateAction<OrderMethod>>
	apply: () => void
	clear: () => void
	toggle: (type: string, value: string, id?: string) => void
	reset: () => void
}

interface ProductListFiltersProviderProps {
	filtersData: UseFiltersData | undefined
	initialParams: ProductListParams | undefined
	resultPathname: string
	onUserApplyFilters?: (serializedFilters: SerializedFilters) => void
	children: ReactNode
}

const noop = () => {
	// Do nothing
}

const defaultContext: ProductListFiltersContextType = {
	selectedFilters: [],
	orderFilter: {
		id: FilterId.subfamilies,
		label: '',
		selection: [],
		choices: [],
		multipleChoice: false,
		options: [],
		type: FilterType.generic,
	},
	selectedOrder: RECOMMENDED,
	selectedCount: 0,
	hasSelection: false,
	disabledValues: [],
	nAvailableFilters: 0,
	nActiveFilters: 0,
	filteredItemIdSet: new Set(),
	currentOrder: RECOMMENDED,
	hasInitialParams: false,
	setSelectedOrder: noop,
	apply: noop,
	clear: noop,
	setSelectedRange: noop,
	toggle: noop,
	reset: noop,
}

export const ProductListFiltersContext =
	createContext<ProductListFiltersContextType>(defaultContext)

export const ProductListFiltersProvider = forwardRef<
	ProductListFiltersRef,
	Readonly<ProductListFiltersProviderProps>
>((params, ref) => {
	const {
		filtersData,
		initialParams,
		resultPathname,
		onUserApplyFilters,
		children,
	} = params

	const { setCount } = useProductList()
	const push = usePushFiltersSearchParams(resultPathname)

	const originalItems = useMemo(
		() => filtersData?.catalogItemsData ?? [],
		[filtersData?.catalogItemsData]
	)

	const [defaultParams, setDefaultParams] = useState(initialParams)

	const { defaultFilters, defaultOrder, defaultRangeLimit, defaultRange } =
		useFiltersDefaultValues({ filtersData, defaultParams })

	const {
		selectedFilters,
		selectedLimitRange,
		selectedOrder,
		selectedRange,
		clear,
		setSelectedRange,
		setSelectedOrder,
		toggle,
		reset,
	} = useFiltersValue({
		defaultFilters,
		defaultOrder,
		defaultRangeLimit,
		defaultRange,
		originalItems,
	})

	const { t } = useLabels()

	const disabledValues = useMemo(
		() =>
			getDisabledValues(
				selectedFilters,
				originalItems,
				selectedRange,
				selectedLimitRange
			),
		[originalItems, selectedFilters, selectedLimitRange, selectedRange]
	)

	const selectedItems = useMemo(
		() => filterItems(selectedFilters, originalItems, selectedRange),
		[originalItems, selectedFilters, selectedRange]
	)

	const selectedCount = selectedItems.length

	const [filteredItemIdSet, setFilteredItemIdSet] = useState(
		new Set(selectedItems.map(({ id }) => id))
	)

	const [currentOrder, setCurrentOrder] = useState(selectedOrder)

	const nAvailableFilters = useMemo(
		() => filtersData?.filters.length ?? 0,
		[filtersData?.filters.length]
	)

	const nActiveFilters = useMemo(
		() =>
			defaultFilters && filtersData
				? countActiveFilters(defaultFilters, defaultRange, filtersData.range)
				: 0,
		[defaultFilters, defaultRange, filtersData]
	)

	const orderFilter = useMemo<UserFilter>(
		() => ({
			type: FilterType.order,
			label: 'product.filters.orderBy.title',
			selection: [selectedOrder],
			multipleChoice: false,
			options: ORDER_OPTIONS.map((option) => ({
				...option,
				label: t(option.label),
			})),
		}),
		[selectedOrder, t]
	)

	const applyFilters = useCallback(
		(filterParams: {
			filters: UserFilter[]
			order: OrderMethod
			range: RangePickerValue | null
			rangeLimit: RangePickerValue | null
			itemIdSet: Set<ProductListItem['id']>
		}) => {
			const serializedFilters = serialize({
				selectedFilters: filterParams.filters,
				selectedOrder: filterParams.order,
				selectedRange: filterParams.range,
				selectedLimitRange: filterParams.rangeLimit,
			})

			// Update the search params
			push(serializedFilters)

			// Update the product list items
			setFilteredItemIdSet(filterParams.itemIdSet)
			setCurrentOrder(filterParams.order)

			// Reset the number of products displayed and the filters selected
			setCount(AFTER_FILTER_PRODUCTS)

			// Do not take into account initialParams anymore
			setDefaultParams(undefined)

			return serializedFilters
		},
		[push, setCount]
	)

	const apply = useCallback(() => {
		const serializedFilters = applyFilters({
			filters: selectedFilters,
			order: selectedOrder,
			range: selectedRange,
			rangeLimit: selectedLimitRange,
			itemIdSet: new Set(selectedItems.map(({ id }) => id)),
		})
		onUserApplyFilters?.(serializedFilters)
	}, [
		selectedFilters,
		selectedItems,
		selectedLimitRange,
		selectedOrder,
		selectedRange,
		applyFilters,
		onUserApplyFilters,
	])

	const hasInitialParams = useMemo(
		() => !!initialParams && Object.values(initialParams).some(Boolean),
		[initialParams]
	)

	const result = useMemo<ProductListFiltersContextType>(() => {
		const hasSelection = getHasSelection(
			selectedFilters,
			selectedOrder,
			selectedRange,
			selectedLimitRange
		)
		return {
			defaultRange,
			disabledValues,
			hasInitialParams,
			hasSelection,
			nAvailableFilters,
			nActiveFilters,
			orderFilter,
			selectedCount,
			selectedFilters,
			selectedLimitRange,
			selectedOrder,
			selectedRange,
			filteredItemIdSet,
			currentOrder,
			apply,
			setSelectedOrder,
			toggle,
			clear,
			setSelectedRange,
			reset,
		}
	}, [
		apply,
		clear,
		reset,
		defaultRange,
		disabledValues,
		filteredItemIdSet,
		currentOrder,
		nActiveFilters,
		nAvailableFilters,
		orderFilter,
		selectedCount,
		selectedFilters,
		selectedLimitRange,
		selectedOrder,
		selectedRange,
		hasInitialParams,
		setSelectedOrder,
		setSelectedRange,
		toggle,
	])

	const clearFilters = useCallback(() => {
		const { filters, order, range, rangeLimit } = clear()
		applyFilters({
			filters,
			order,
			range,
			rangeLimit,
			itemIdSet: new Set(originalItems.map(({ id }) => id)),
		})
	}, [clear, applyFilters, originalItems])

	useImperativeHandle(ref, () => ({ clearFilters }), [clearFilters])

	return (
		<ProductListFiltersContext.Provider value={result}>
			{children}
		</ProductListFiltersContext.Provider>
	)
})
